import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { fadeAndSlideUp } from '@smartera/animations';
import { Oauth2Service, TokenService, UserService } from '@api/services';
import { AuthenticationUrls } from '../models';
import { LocalStorageService } from '@smartera/core';
import { UtilityService } from '@core/services/utility.service';
import { BaseComponent } from '@modules/components/base-component';
import { catchError, throwError } from 'rxjs';
import { LoginParametersInfo, RegisterResponse } from '../models';
@Component({
	selector: 'app-register-password',
	templateUrl: './register-password.component.html',
	styleUrls: ['./register-password.component.scss'],
	animations: [fadeAndSlideUp],
	standalone: false,
})
export class RegisterPasswordComponent extends BaseComponent implements OnInit {
	private registerPath: string = AuthenticationUrls.FirstPassword;
	public form: FormGroup;
	loginForm: FormGroup;
	public registerKey: string;
	public registerEmail: string;
	public errorMessage: string;
	public changeErrorMessage: string;
	public forgotMessage: string;
	loginParametersInfo: LoginParametersInfo;
	timer: number;
	timerForRetry: number;
	mainCountDown: ReturnType<typeof setInterval>;
	retryCountDown: ReturnType<typeof setInterval>;
	isRetryTimerEnds: boolean = false;
	isExpired: boolean = false;

	constructor(
		private _http: HttpClient,
		private _route: ActivatedRoute,
		private _router: Router,
		private tokenService: TokenService,
		private userService: UserService,
		private localStorageService: LocalStorageService,
		private oauth2Service: Oauth2Service,
		utilServ: UtilityService
	) {
		super(null, utilServ);

		this.form = this.builder.group(
			{
				password: ['', [Validators.required]],
				confirmPassword: ['', [Validators.required]],
			},
			{ validator: passwordMatchValidator }
		);
		this.loginForm = this.utilServ.builder.group({
			code: [null, [Validators.required]],
		});

		function passwordMatchValidator(g: FormGroup): { mismatch: boolean } {
			return g.get('password').value === g.get('confirmPassword').value ? null : { mismatch: true };
		}
	}

	public ngOnInit(): void {
		this.hideloader();
	}

	public getRegisterControlsState(formControlName: string): boolean {
		return (
			this.form.get(formControlName).invalid &&
			(this.form.get(formControlName).dirty || this.form.get(formControlName).touched)
		);
	}

	public register(value: { password: string; confirmPassword: string }): void {
		this._route.params.subscribe((params: Params) => (this.registerEmail = params.email));
		this._route.params.subscribe((params: Params) => (this.registerKey = params.activation));
		this._http
			.get(
				this.registerPath +
					'?email=' +
					this.registerEmail +
					'&key=' +
					encodeURIComponent(this.registerKey) +
					'&password1=' +
					encodeURIComponent(value.password) +
					'&password2=' +
					encodeURIComponent(value.confirmPassword)
			)
			.subscribe((data: RegisterResponse) => {
				this.utilServ.alertService.success(this.t('alert.title.success'), data.message);
				this.loginAfterRegister(this.registerEmail, value.password);
			});
	}
	private loginAfterRegister(email: string, password: string): void {
		const formData: { email: string; password: string } = { email, password };
		this.showLoader();

		this.oauth2Service
			.login(formData.email, formData.password)
			.pipe(
				catchError((e) => {
					const err: string = e.error.code;
					console.log(err);
					if (err === 'login.renew.password.exception') {
						this._router.navigate(['login/expire-password', formData.email]);
					} else {
						this.utilServ.alertService.error(this.t('failedLoginErrorMessage'), err);
					}
					return throwError(err);
				})
			)
			.subscribe(() => {
				this.tokenService.isTokenExpired.next(false);
				this.userService.getLoginParameters().subscribe(async (res) => {
					if (res.user2FARequired == true) {
						this.isDialogVisible = true;
						this.loginParametersInfo = res;
						this.startCountDown(res.user2FAExpirePeriodInSecond);
						this.startCountDownForRetry(res.user2FAExpirePeriodInSecond);
						this.hideloader();
					} else {
						await this.oauth2Service
							.fetchAndSetStorageUserInfo()
							.then(() => {
								this._router.navigateByUrl('/page/dashboard');
							})
							.finally(() => {
								this.hideloader();
							});
					}
				});
			});
	}
	private startCountDown(second: number): void {
		this.timer = second;
		this.mainCountDown = setInterval(() => {
			this.timer = this.timer - 1;
			if (this.timer == 0) {
				this.isExpired = true;
				clearInterval(this.mainCountDown);
			}
		}, 1000);
	}

	private startCountDownForRetry(second: number): void {
		this.timerForRetry = second;
		this.retryCountDown = setInterval(() => {
			this.timerForRetry = this.timerForRetry - 1;
			if (this.timerForRetry == 0) {
				this.isRetryTimerEnds = true;
				clearInterval(this.retryCountDown);
			}
		}, 1000);
	}
	control(): void {
		const loginObject = {
			code: this.loginForm.get('code').value,
		};
		this.tokenService.unlockUser2Fa(loginObject).subscribe(async () => {
			this.showLoader();
			await this.oauth2Service
				.fetchAndSetStorageUserInfo()
				.then(() => {
					this._router.navigateByUrl('/page');
				})
				.finally(() => {
					this.hideloader();
				});
		});
	}

	resetAll(): void {
		this.loginForm.reset();
		this.isRetryTimerEnds = false;
	}

	retryToConnect(): void {
		this.tokenService.resend2Fa().subscribe(() => {
			this.userService.getLoginParameters().subscribe((res) => {
				clearInterval(this.mainCountDown);
				clearInterval(this.retryCountDown);
				this.startCountDown(res.user2FAExpirePeriodInSecond);
				this.isRetryTimerEnds = false;
				this.startCountDownForRetry(res.user2FAExpirePeriodInSecond);
			});
		});
	}
}
